import { isFunction as lo_isFunction } from 'es-toolkit';
import { noop } from '#x1-base/utils';

const now = () => performance.now();

type TimerCallback = (dt: number) => void;

export class Timer extends EventTarget {
    public count: number = 0;

    private running: boolean = false;
    private last: number = 0;
    private target: number = 0;
    private _interval: number = 1000;
    private timeoutId: Nullable<ReturnType<typeof setTimeout>> = null;
    private fn: TimerCallback = noop;
    private fireFn: TimerCallback = noop;

    constructor(fn?: TimerCallback, interval = 1000) {
        super();
        this.last = now();
        this.fn = fn || noop;
        this._interval = interval;
        this.fireFn = (dt: number) => {
            if (lo_isFunction(this.fn)) {
                this.fn.call(this, dt);
            }
        };
    }

    public get interval(): number {
        return this._interval;
    }

    public set interval(value: number) {
        this._interval = value;
    }

    public start(fn?: TimerCallback): void {
        if (this.running) {
            return;
        }

        if (lo_isFunction(fn)) {
            this.fn = fn;
        }

        this.running = true;
        this.last = now();
        this.target = this.last;
        this.schedule();
        this.dispatchEvent(new Event('start'));
    }

    public stop(): void {
        if (!this.running) {
            return;
        }

        this.running = false;

        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        this.dispatchEvent(new Event('stop'));
    }

    public reset(): void {
        const wasRunning = this.running;
        this.stop();
        this.count = 0;
        this.last = now();
        this.dispatchEvent(new Event('reset'));

        if (wasRunning) {
            this.start();
        }
    }

    public destroy(): void {
        this.stop();
        this.fn = noop;
        this.fireFn = noop;
    }

    /*
        Schedules against an absolute target so setTimeout lateness doesn't
        accumulate across ticks.
    */
    private schedule(): void {
        const time = now();
        this.target += this._interval;

        // If we fell more than one interval behind (page suspended/throttled,
        // long task), skip the missed ticks instead of rapid-firing to catch
        // up.
        if (this.target < time) {
            this.target = time + this._interval;
        }

        this.timeoutId = setTimeout(() => this.tick(), this.target - time);
    }

    private tick(): void {
        if (!this.running) {
            return;
        }

        const time = now();
        const dt = time - this.last;

        this.last = time;
        this.count++;
        this.fireFn(dt);
        this.dispatchEvent(new Event('tick'));

        if (this.running) {
            this.schedule();
        }
    }
}
